import { FC, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Flex, Image } from '../../components';
import DefaultLogo from '../../components/Logo/DefaultLogo';
import { BoxProps } from '@urbaninfrastructure/react-ui-kit';
import { useSystemConfig } from '../../lib/system-config-context';
import { urbansharingAssetsPath } from '../../lib/constants';
import { dinamicRenderingConfig } from '../../lib/server/middlewares/system-config/dinamic-rendering';

const ICON_SIZE = 80;
const PADDING = ICON_SIZE / 5;
const LOGO_SIZE = ICON_SIZE / 1.5;

const px = (val: number) => `${val}px`;
const getTogether = ICON_SIZE / 2 - PADDING / 2;

const ghostVariants = {
  spread: ({ first }) => {
    const s = PADDING;
    return {
      x: first ? px(-s) : px(s),
      transition: {
        duration: 0.3,
        delay: 1
      }
    };
  },
  together: ({ first }) => {
    const s = getTogether;
    return {
      x: first ? px(s) : px(-s)
    };
  }
};

const AppIcon: FC<BoxProps> = ({ bg = 'primary', ...props }) => (
  <Flex
    // overflow="hidden"
    size={ICON_SIZE}
    borderRadius="12px"
    bg={bg}
    color={'white' as any}
    alignItems="center"
    justifyContent="center"
    {...props}
  />
);

const AppLogo = () => {
  const systemConfig = useSystemConfig();
  const setAppIconPadding = dinamicRenderingConfig.find(
    config => config.systemId === systemConfig.id
  )?.appIconPadding;

  if (systemConfig.faviconKey) {
    const src =
      urbansharingAssetsPath +
      `/v1/favicons/public-web/${systemConfig.faviconKey}/android-chrome-256x256.png`;

    return (
      <AppIcon p={setAppIconPadding ? 2 : 0} bg="transparent" boxShadow="light">
        <Image borderRadius="12px" src={src} alt="" />
      </AppIcon>
    );
  }

  return (
    <AppIcon>
      <DefaultLogo width={LOGO_SIZE} height={LOGO_SIZE} />
    </AppIcon>
  );
};

function AppIconAnimation() {
  const [animateInIcon, setAnimateInIcon] = useState(false);
  const reduceMotion = useReducedMotion();
  const initial = reduceMotion ? 'spread' : 'together';

  return (
    <Flex justifyContent="center" mb="sm">
      <motion.div
        initial={initial}
        variants={ghostVariants}
        animate="spread"
        custom={{ first: true }}
        onAnimationComplete={() => {
          setAnimateInIcon(true);
        }}
      >
        <AppIcon bg="neutral.2" />
      </motion.div>
      <motion.div
        initial="hidden"
        animate={animateInIcon ? 'visible' : 'hidden'}
        variants={{
          visible: {
            scale: 1,
            opacity: 1
          },
          hidden: {
            scale: !reduceMotion ? 0 : 1,
            opacity: 0
          }
        }}
      >
        <AppLogo />
      </motion.div>
      <motion.div
        initial={initial}
        variants={ghostVariants}
        animate="spread"
        custom={{ first: false }}
      >
        <AppIcon bg="neutral.2" />
      </motion.div>
    </Flex>
  );
}

export default AppIconAnimation;
